import { type ReactNode } from 'react'

import Layout from '@core/layouts/Layout'

import useIsReadOnly from '~/auth/hooks/useIsReadOnly'
import { KeyboardShortcutsOverlay, PageShortcutsProvider, SequenceIndicator } from '~/components/KeyboardShortcuts'
import { LoadingMessage } from '~/components/Loaders'
import { FeatureGate, useGateValue } from '~/configs/statsig'
import { useActualOrg, useCurrentOrgHasInsights } from '~/data/api/hooks/organizations'
import { useCurrentUser } from '~/data/api/hooks/users'
import { useBootMark } from '~/debug/bootProfiler'
import AssistantV2 from '~/features/assistant-v2'
import AssistantBar from '~/features/assistant/AssistantBar'
import { AssistantRunSkillProvider } from '~/features/assistant/AssistantRunSkillContext'
import { useIsFullscreenMode } from '~/hooks/useFullscreenMode'
import useKeyboardShortcuts from '~/hooks/useKeyboardShortcuts'
import { useMediaQuery } from '~/theme/hooks'

import InterstitialPage from './components/InterstitialPage'

interface Props {
  children: ReactNode
}

// Per-org entitlement gate for the assistant: only mounts the bar when the
// org has Insights enabled. `hasInsights` is `boolean | undefined`, so a
// `false` or absent (null/undefined) value resolves to "off". Read-only users
// are denied the assistant entirely — it can execute skills that mutate data.
// Fullscreen ("monitor") mode also suppresses it — nothing should float over a
// wall display. `assistant-v2` picks which surface the entry point opens.
const AssistantBarGate = () => {
  const hasInsights = useCurrentOrgHasInsights()
  const readOnly = useIsReadOnly()
  const isFullscreen = useIsFullscreenMode()
  const isAssistantV2 = useGateValue('assistant-v2')
  if (!hasInsights || readOnly || isFullscreen) return null
  return isAssistantV2 ? <AssistantV2 /> : <AssistantBar />
}

// Only mounted once the full layout renders, so the global keydown listener is
// not installed during loading / interstitial states (where the help overlay
// wouldn't be visible anyway).
const KeyboardShortcutsHost = ({ children }: { children: ReactNode }) => {
  const { shortcutsOverlay, sequence, dismissSequence } = useKeyboardShortcuts()
  return (
    <>
      <AssistantRunSkillProvider>
        {children}
        <FeatureGate feature="ai-assistant">
          <AssistantBarGate />
        </FeatureGate>
      </AssistantRunSkillProvider>
      <KeyboardShortcutsOverlay {...shortcutsOverlay} />
      <SequenceIndicator sequence={sequence} dismissSequence={dismissSequence} />
    </>
  )
}

const UserLayout = ({ children }: Props) => {
  const { synopUser, isUserLoading } = useCurrentUser()
  // We don't read the org here, but we want to hold off rendering most of the UI until it's available.
  const { isLoading: isOrgLoading } = useActualOrg()

  /**
   * The below variable will hide the nav drawer below a given screen size. The menu will be
   * accessible from the hamburger icon.
   */
  const hidden = useMediaQuery((theme) => theme.breakpoints.down('lg'))

  const layoutWillRender = !isUserLoading && !isOrgLoading && (!synopUser || !!synopUser.termsAccepted)
  useBootMark({ phase: 'org-resolved', when: !isOrgLoading })
  useBootMark({ phase: 'first-content-paint', when: layoutWillRender, report: true })

  if (isUserLoading || isOrgLoading) return <LoadingMessage />
  if (synopUser && !synopUser.termsAccepted) return <InterstitialPage />
  return (
    <Layout hidden={hidden}>
      <PageShortcutsProvider>
        <KeyboardShortcutsHost>{children}</KeyboardShortcutsHost>
      </PageShortcutsProvider>
    </Layout>
  )
}

export default UserLayout
